// eyeq — shipment canon v2 (ADR-0051). Pure, side-effect-free helpers shared by
// the worker's shipment builder and the internal cabinet's xlsx writer, so the
// two can never disagree about a name, an id or a taxonomy spelling.
//
// Three dialects of the same taxonomy coexist and each one is load-bearing:
//   · the JSON-schema dialect ("Fine Art and Museum") — per-file delivery JSON,
//     pinned by MeqAssetMetaSchema;
//   · the underscore dialect ("Fine_Art_Museum") — the xlsx template's own
//     validation lists (its INDIRECT formulas resolve defined names spelled
//     this way), so the table MUST use it;
//   · our internal cat_id slug ("fine_art_and_museum__paintings") — D1 only.
// eyeqUnderscoreName converts dialect 1 → dialect 2 and nothing else.
import { z } from 'zod';
import { eyeqUrlFormatNote } from './upload';

/** Name → the template's underscore spelling. Rules derived from the template's
 *  own defined names: split on whitespace, comma, hyphen and slash; drop the
 *  connector word "and"; join with '_'. Apostrophes stay ("Children's_Toys").
 *  Verified against all 33 categories and 182 sub-categories. */
/** Имена категорий и подкатегорий в поставке — РОВНО как в JSON-схеме
 *  заказчика (`mobile_eyeq.schema`, наш `MeqCategorySchema`): «Pets and
 *  Animals», «Signs, Symbols and Icons». Подтверждено заказчиком 2026-09-08
 *  (ADR-0090): «the JSON schema names work the best». Замена «and» → «&»
 *  из ADR-0084 отменена; функция оставлена как единственная точка, где
 *  поставка могла бы менять имя, и намеренно ничего не меняет. */
export function eyeqClientName(name: string): string {
  return name;
}

export function eyeqUnderscoreName(name: string): string {
  return name
    .split(/[\s,\-/]+/)
    .filter((t) => t.length > 0 && t.toLowerCase() !== 'and')
    .join('_');
}

// Client shipment table header row — VERBATIM from the current template's
// ListObject columns. The two "Lightining" typos of the 2026-07 template are
// FIXED in this one; reproduce whatever the template says, never a guess.
export const EYEQ_SHIPMENT_HEADERS_V2 = [
  'Asset_ID', 'subject_name', 'disambiguation_details', 'category',
  'sub_category', 'indoor_outdoor', 'Indoor Lighting', 'Outdoor Lighting',
  'place_details', 'url', 'has_text', 'Language_Code', 'city', 'country',
  'camera_make', 'camera_model', 'filename', 'Participant ID', 'UUID',
] as const;

/** Delivery file name. Numbering restarts at 1 in EVERY shipment and the order
 *  is (Participant ID, UUID) — the client reads the folder as a sequence, so a
 *  gap or a global counter both read as a packing defect. */
export function eyeqShipmentFileName(seq: number, ext: string): string {
  return `IMG_${String(seq).padStart(4, '0')}.${ext || 'bin'}`;
}

/** Asset_ID = <Participant ID>_<file stem>_<UUID>. Composed, never stored as a
 *  free field: every part must stay derivable from the row itself. */
export function eyeqDeliveryAssetId(pid: string, fileName: string, uuidSeq: number): string {
  const stem = fileName.replace(/\.[^.]*$/, '');
  return `${pid}_${stem}_${uuidSeq}`;
}

/** Participant ID out of a locale pool: 4-letter prefix + zero-padded index. */
export function eyeqPid(prefix: string, index: number, width = 5): string {
  return prefix + String(index).padStart(width, '0');
}

/** Pool prefix for a locale — the underscore form uppercased ('pl_PL' → 'PLPL'). */
export function eyeqPidPrefix(locale: string): string {
  return locale.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** Index back out of a Participant ID (''/malformed → null). */
export function eyeqPidIndex(pid: string): number | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(pid.trim());
  if (!m) return null;
  return Number(m[2]);
}

/** Обратный разбор Asset_ID заказчика (<pid>_<stem>_<uuid_seq>) — инверсия
 *  eyeqDeliveryAssetId. Матчинг вердиктов идёт по (delivery_pid, uuid_seq)
 *  через индекс, а не по склейке строк в SQL. stem сам содержит '_'
 *  (IMG_0042), поэтому pid — префикс буквы+цифры, uuid_seq — хвостовые цифры
 *  после ПОСЛЕДНЕГО '_'. Malformed → null, никогда не догадка. */
export function eyeqParseDeliveryAssetId(
  s: string,
): { pid: string; stem: string; uuid_seq: number } | null {
  const m = /^([A-Za-z]+\d+)_(.+)_(\d+)$/.exec(s.trim());
  if (!m) return null;
  return { pid: m[1], stem: m[2], uuid_seq: Number(m[3]) };
}

// disambiguation_details is a description of the OBJECT. Three QA rounds kept
// finding annotator voice in it instead — "not the target", "the target object
// is …", "this image shows …". Those phrases are meta-commentary about the
// task, they ship straight into the client's table, and they are detectable
// deterministically, so they are a preflight issue rather than a QA opinion.
// Канон A/B переписи v5 (принят заказчиком в поставках v5+): форма A
// «X is the target object, while Y are not the primary target» ЛЕГАЛЬНА —
// запрещено только повествование от лица кадра. Прежний реестр запрещал и
// 'the target', чем противоречил самому канону (вскрыто 2026-09-01 при
// сборке серверной поставки).
const DETAILS_META_PHRASES = [
  'this image',
  'this photo',
  'the photo shows',
  'the image shows',
  'in this picture',
  'in the picture',
];

/** null | 'details_style'. */
export function eyeqDetailsStyleNote(details: string): string | null {
  const s = details.toLowerCase();
  return DETAILS_META_PHRASES.some((p) => s.includes(p)) ? 'details_style' : null;
}

/** Google Maps search link for a Business and Storefronts subject — the URL
 *  form the Capture Protocol itself sanctions for that category. Built from
 *  the NAME + city + country, never from coordinates: the index deliberately
 *  keeps location at city/country granularity (ADR-0039 §2), and a link that
 *  carried lat/lon would put precise geolocation back into D1. */
export function eyeqMapsSearchUrl(subject: string, city: string, country: string): string {
  const q = [subject, city, country].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
}

// One shipment row as the ship endpoints emit it (v2 fields carry the canon:
// pool id, per-participant sequence number, delivery file name and Asset_ID).
export const EyeqShipRowSchema = z.object({
  asset_id: z.string(),          // our catalog id ea_<32hex>
  uuid: z.string(),              // RFC-4122 v4 minted at submit (audit trail)
  r2_key: z.string(),
  shipped_file: z.string(),      // IMG_0001.heic
  shipped_json: z.string(),      // IMG_0001.json
  original_filename: z.string(),
  participant_public_id: z.string(), // ep_<12hex> — our opaque participant key
  pid: z.string(),               // Participant ID in the client's pool
  uuid_seq: z.number().int(),    // per-participant running number
  delivery_asset_id: z.string(), // <pid>_IMG_0001_<uuid_seq>
  json: z.record(z.string(), z.unknown()),
});
export type EyeqShipRow = z.infer<typeof EyeqShipRowSchema>;

// ── POST /api/eyeq/delivery/register (machine) — ADR-0056 ───────────────────
// Records a delivery that was assembled OUTSIDE the worker (the local
// builders of 2026-08) or re-sent (rework) after the fact: one shipment header
// + the assets it carried, each with the name / PID / UUID and the per-file
// JSON exactly as the client received them. Idempotent per
// (asset, shipment, kind, delivered_at); re-posting the same folder is a no-op.
const YMD = /^\d{4}-\d{2}-\d{2}$/;
export const EyeqDeliveryKindSchema = z.enum(['initial', 'rework']);
export const EyeqClientStatusSchema = z.enum(['', 'accepted', 'rejected']);

// Норматив имени отгрузки (ADR-0082, уточнён ADR-0084) — ровно имя папки,
// которую получает клиент: с 2026-09-03 — `YYYYMMDDb1`, `YYYYMMDDb2`, … (b1
// всегда, даже если отгрузка за день одна: имя известно в момент сборки и
// папку не приходится переименовывать); исторические `YYYYMMDD` без суффикса
// остаются; `Rework_<исходное имя>` для повторной отправки (`Rework_20260903b1`).
// Второй и далее реворк той же партии — `Rework2_<исходное имя>`. Исторические имена (Rework20260812, Rework_20260810,
// eyeq_delivery_2026-07-31) допускаются как есть — под ними партии уже лежат
// у клиента. Никаких выдуманных префиксов вроде `ship_`.
export const EYEQ_SHIPMENT_ID_RE = /^(?:\d{8}(?:b\d{1,2})?|Rework\d*_?\d{8}(?:b\d{1,2})?|eyeq_delivery_\d{4}-\d{2}-\d{2})$/;
export const eyeqIsShipmentId = (s: string): boolean => EYEQ_SHIPMENT_ID_RE.test(s);
/** Имя реворка по нормативу: n=1 → Rework_<исходная>, n≥2 → Rework<n>_<исходная>. */
export function eyeqReworkId(originalId: string, n = 1): string {
  const base = /(\d{8}(?:b\d{1,2})?)$/.exec(originalId)?.[1] ?? originalId.replace(/\D/g, '').slice(0, 8);
  return n <= 1 ? `Rework_${base}` : `Rework${n}_${base}`;
}
/** Имя N-й отгрузки за день: всегда `YYYYMMDDbN` (b1 — первая). */
export function eyeqDayShipmentId(ymd: string, n: number): string {
  return `${ymd}b${Math.max(1, n)}`;
}

// Стадии как в трекере клиента (колонка Status + PM Review).
export const EyeqShipmentStageSchema = z.enum(['submitted', 'for_revision', 'uploaded_box', 'for_upload_s3', 'uploaded_s3']);
export const EyeqPmReviewSchema = z.enum(['', 'accepted', 'rejected']);

export const EyeqDeliveryShipmentSchema = z.object({
  shipment_id: z.string().regex(EYEQ_SHIPMENT_ID_RE),
  session_id: z.string().max(80).default(''),      // name on the client's Deliveries sheet
  shipped_at: z.string().regex(YMD),
  kind: EyeqDeliveryKindSchema.default('initial'),
  supersedes: z.string().max(60).default(''),
  reworked_at: z.string().regex(YMD).or(z.literal('')).default(''),
  source: z.string().max(120).default(''),         // local folder / 'worker'
  note: z.string().max(500).default(''),
  // Стадии трекера: пустое значение при повторной регистрации НЕ затирает
  // уже записанное (заголовок апсертится идемпотентно).
  pm_review: EyeqPmReviewSchema.default(''),
  pm_note: z.string().max(300).default(''),
  stage: EyeqShipmentStageSchema.or(z.literal('')).default(''),
  s3_at: z.string().regex(YMD).or(z.literal('')).default(''),
  redelivered_at: z.string().regex(YMD).or(z.literal('')).default(''),
  rejected_count: z.number().int().nonnegative().default(0),
});

export const EyeqDeliveryItemSchema = z.object({
  asset_id: z.string().regex(/^ea_[0-9a-f]{32}$/),
  delivered_at: z.string().regex(YMD),
  kind: EyeqDeliveryKindSchema.default('initial'),
  delivery_file: z.string().min(1).max(200),
  delivery_pid: z.string().max(20).default(''),
  uuid_seq: z.number().int().nonnegative().default(0),
  meta: z.record(z.string(), z.unknown()).default({}),
  client_status: EyeqClientStatusSchema.default(''),
  client_note: z.string().max(500).default(''),
  note: z.string().max(500).default(''),
  /** Registry hints for the PID backfill (city / camera the PID was minted for). */
  pid_city: z.string().max(120).default(''),
  pid_camera: z.string().max(120).default(''),
});

export const EyeqDeliveryRegisterReqSchema = z.object({
  actor: z.string().min(1).max(80),
  shipment: EyeqDeliveryShipmentSchema,
  items: z.array(EyeqDeliveryItemSchema).max(20).default([]),
  /** Mark the asset as shipped + freeze delivery identity on eyeq_asset_meta
   *  (the latest shipment wins). Off for a pure history backfill. */
  apply_to_assets: z.boolean().default(true),
});
export type EyeqDeliveryRegisterReq = z.infer<typeof EyeqDeliveryRegisterReqSchema>;

export const EyeqDeliveryRegisterResSchema = z.object({
  shipment_id: z.string(),
  recorded: z.number().int(),   // new history rows
  repeated: z.number().int(),   // rows that already existed
  unknown: z.array(z.string()), // asset_ids the catalog does not have
  asset_count: z.number().int(),// distinct assets on the shipment after this call
  pids_upserted: z.number().int(),
});
export type EyeqDeliveryRegisterRes = z.infer<typeof EyeqDeliveryRegisterResSchema>;

// ── POST /api/eyeq/delivery/clientVerdicts (machine) — ADR-0072 ─────────────
// Ввод вердиктов заказчика по отгруженному. До этого эндпоинта
// client_accepted/client_rejected были объявлены в статусном вокабуляре, но
// НЕДОСТИЖИМЫ: ни один хендлер их не писал. Каждый item указывает ровно одну
// отправку одним из четырёх ключей; резолвинг идёт по порядку ниже, первый
// разрешившийся побеждает, догадок нет (0 матчей → unknown, >1 → ambiguous —
// per-item ошибки, батч не падает).
export const EyeqClientVerdictItemSchema = z.object({
  /** 1) наш каталожный id → ПОСЛЕДНЯЯ отправка ассета. */
  asset_id: z.string().regex(/^ea_[0-9a-f]{32}$/).optional(),
  /** 2) (shipment_id, delivery_file). */
  shipment_id: z.string().max(60).optional(),
  /** 3) (session_id листа Deliveries заказчика, delivery_file). */
  session_id: z.string().max(80).optional(),
  delivery_file: z.string().max(200).optional(),
  /** 4) Asset_ID заказчика — разбирается eyeqParseDeliveryAssetId,
   *  матч по (delivery_pid, uuid_seq). */
  delivery_asset_id: z.string().max(120).optional(),
  client_status: z.enum(['accepted', 'rejected']),
  client_note: z.string().max(500).default(''),
});
export type EyeqClientVerdictItem = z.infer<typeof EyeqClientVerdictItemSchema>;

export const EyeqClientVerdictsReqSchema = z.object({
  actor: z.string().min(1).max(80),
  /** Откуда вердикты (имя листа/письма заказчика) — уходит в meta_json
   *  аудита. Актор при этом machine:<actor>, НЕ client:<label>: заказчик у
   *  нас не аутентифицировался, его таблицу пересказал машинный вызов. */
  client_label: z.string().max(200).default(''),
  items: z.array(EyeqClientVerdictItemSchema).min(1).max(50),
});
export type EyeqClientVerdictsReq = z.infer<typeof EyeqClientVerdictsReqSchema>;

export const EyeqClientVerdictsResSchema = z.object({
  ok: z.literal(true),
  applied: z.number().int(),
  unchanged: z.number().int(), // повтор того же вердикта — идемпотентный no-op
  errors: z.array(z.object({ ref: z.string(), note: z.string() })),
  cells_recomputed: z.number().int(),
});
export type EyeqClientVerdictsRes = z.infer<typeof EyeqClientVerdictsResSchema>;

// ── Гейт отгрузки (ADR-0091, 2026-09-08) ─────────────────────────────────────
// ОДНО правило «может ли строка уехать» для всех входов: сборка воркером
// (planShipment), регистрация собранного снаружи (delivery/register),
// предпросмотр в кабинете и локальные сборщики. До этого поставка 20260827 ушла
// с 1 107 кадрами без обязательной ссылки: гейт стоял только в planShipment, а
// регистрация принимала что угодно. Чистая функция без БД: кто-то должен
// принести правило ячейки (url_required) вместе со строкой.
export const EYEQ_SHIP_GATE_ISSUES = [
  'not_qa_accepted', 'details_style', 'url_missing', 'url_in_nourl_cell', 'na_url', 'url_maps', 'url_format',
] as const;
export type EyeqShipGateIssue = (typeof EYEQ_SHIP_GATE_ISSUES)[number];

export interface EyeqShipGateInput {
  qa_verdict: string;
  url: string;
  disambiguation_details: string;
  /** Правило ячейки из eyeq_quota; undefined = ячейка не в плане (ссылка не проверяется по обязательности). */
  url_required: boolean | undefined;
}

/** Ссылка на карты — запрещена в поставке (владелец, 2026-09-08): любой
 *  хост Google Maps и короткие ссылки карт, включая протокольное
 *  исключение `maps.app.goo.gl`, которое приём пока терпит. */
export function eyeqUrlIsMaps(url: string): boolean {
  const s = url.trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'maps.app.goo.gl' || host === 'maps.google.com') return true;
    if (/^google\.[a-z.]+$/.test(host) && /^\/maps(\/|$)/.test(u.pathname)) return true;
    if (host === 'goo.gl' && /^\/maps/.test(u.pathname)) return true;
    return false;
  } catch {
    return false;
  }
}

/** null — строка годна к отгрузке; иначе первая причина по порядку дешевизны.
 *  Обязательная ссылка не может быть пустой, «N/A», картами или неверного
 *  формата; ячейка без ссылки не принимает ссылку вовсе. */
export function eyeqShipGate(i: EyeqShipGateInput): EyeqShipGateIssue | null {
  if (i.qa_verdict !== 'accepted') return 'not_qa_accepted';
  if (eyeqDetailsStyleNote(i.disambiguation_details)) return 'details_style';
  const url = (i.url || '').trim();
  const isNa = url === 'N/A' || url === 'NA';
  if (i.url_required === true && !url) return 'url_missing';
  if (i.url_required === true && isNa) return 'na_url';
  if (!url || isNa) return null; // пусто или «N/A» вне обязательной ячейки — законно
  if (i.url_required === false) return 'url_in_nourl_cell';
  if (eyeqUrlIsMaps(url)) return 'url_maps';
  if (eyeqUrlFormatNote(url)) return 'url_format';
  return null;
}

/** Ответ 422 регистрации: ни одна строка не записана. */
export const EyeqDeliveryRegisterRefusedSchema = z.object({
  error: z.literal('ship_gate'),
  gate_failed: z.array(z.object({ asset_id: z.string(), issue: z.enum(EYEQ_SHIP_GATE_ISSUES) })),
});
export type EyeqDeliveryRegisterRefused = z.infer<typeof EyeqDeliveryRegisterRefusedSchema>;
